/**
 * Uma folha A4 paisagem de um setor (Compras, Máquinas, Marcenaria,
 * Terceiros, Acabamento ou Expedição) de um lote. Desenho aprovado na
 * fábrica: não mudar medidas nem cores sem o PCP.
 */
import React from "react";

import {
  AJUSTE_INICIAL,
  SETORES,
  TERCEIRA_CELULA,
  ULTIMO_DEGRAU,
  TIPO_COLUNA,
  diaDaSemanaPorExtenso,
  formatarData,
} from "../../../utils/apontamentos";
import {
  BlocoTitulo,
  Cabecalho,
  Cartao,
  Celula,
  DataNaCelula,
  Descricao,
  Detalhe,
  Divisor,
  Faixa,
  Linha,
  LinhaCabecalho,
  Logo,
  NaoSeAplica,
  PAPEL,
  Painel,
  Papel,
  Ponto,
  Quantidade,
  Rodape,
  CampoRodape,
  Rotulo,
  SeloLote,
  Sobretitulo,
  Tabela,
  Titulo,
  Trilha,
  Valor,
  Observacoes,
  MarcaDeVia,
  Pauta,
} from "./styles";

const ALTURA_OBSERVACOES_COMPACTA = 56;

const NUMERO = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 });

export function formatarNumero(valor) {
  return Number.isFinite(valor) ? NUMERO.format(valor) : "";
}

function plural(n, um, varios) {
  return `${formatarNumero(n)} ${n === 1 ? um : varios}`;
}

export function LogoDaUnidade({ logo }) {
  return (
    <Logo>
      {logo?.src ? <img src={logo.src} alt={logo.nome || "Logo"} /> : <span>{logo?.nome || ""}</span>}
    </Logo>
  );
}

function TrilhaDoFluxo({ setorAtual }) {
  return (
    <Trilha $cor={setorAtual.cor.accent} aria-label="Fluxo da fábrica">
      {SETORES.map((setor) => (
        <li key={setor.id}>
          <span className={setor.id === setorAtual.id ? "atual" : undefined}>{setor.curto}</span>
        </li>
      ))}
    </Trilha>
  );
}

export function Selo({ cor, numero }) {
  return (
    <SeloLote $cor={cor} $numeroLongo={String(numero).length > 4}>
      <span className="rotulo">Lote</span>
      <span className="numero">{numero}</span>
    </SeloLote>
  );
}

function TerceiraCelula({ setor, cabecalho }) {
  const { totalM2, totalPecas, totalAmbientes, itens } = cabecalho;
  if (setor.terceiraCelula === TERCEIRA_CELULA.ITENS) {
    return (
      <Celula>
        <Rotulo>Itens de terceiros</Rotulo>
        <Valor>{plural(itens?.itens || 0, "item", "itens")}</Valor>
        <Detalhe>{plural(itens?.ambientes || 0, "ambiente", "ambientes")}</Detalhe>
      </Celula>
    );
  }
  const detalhe = setor.terceiraCelula === TERCEIRA_CELULA.TOTAL_E_ITENS
    ? `${plural(totalPecas, "peça", "peças")} · ${plural(itens?.itens || 0, "item", "itens")} no ${setor.curto.toLowerCase()}`
    : `${plural(totalPecas, "peça", "peças")} · ${plural(totalAmbientes, "ambiente", "ambientes")}`;
  return (
    <Celula>
      <Rotulo>Total do lote</Rotulo>
      <Valor>
        {formatarNumero(totalM2)} m<sup>2</sup>
      </Valor>
      <Detalhe>{detalhe}</Detalhe>
    </Celula>
  );
}

function CartaoData({ rotulo, iso, fundo, comPonto }) {
  return (
    <Cartao $fundo={fundo}>
      <Rotulo $claro>
        {comPonto && <Ponto aria-hidden="true" />}
        {rotulo}
      </Rotulo>
      <div className="data">{formatarData(iso) || "—"}</div>
      <div className="dia">{diaDaSemanaPorExtenso(iso)}</div>
    </Cartao>
  );
}

function DataComDia({ iso, forte }) {
  if (!iso) return null;
  return (
    <DataNaCelula $forte={forte}>
      <span className="data">{formatarData(iso)}</span>
      <span className="dia">{diaDaSemanaPorExtenso(iso)}</span>
    </DataNaCelula>
  );
}

function ConteudoDaCelula({ coluna, valor, setor }) {
  switch (valor.tipo) {
    case "vazio":
      return null;
    case "nao_aplica":
      return <NaoSeAplica>×</NaoSeAplica>;
    case "numero":
      return formatarNumero(valor.valor);
    case "data":
      if (coluna.tipo === TIPO_COLUNA.MANUAL) return formatarData(valor.iso);
      return (
        <DataComDia
          iso={valor.iso}
          forte={coluna.tipo === TIPO_COLUNA.LIMITE || coluna.tipo === TIPO_COLUNA.ENTREGA}
        />
      );
    case "qtd":
      if (coluna.tipo !== TIPO_COLUNA.ITEM_DESCRITO) return formatarNumero(valor.qtd);
      return (
        <>
          <Quantidade $cor={setor.cor.deep}>{formatarNumero(valor.qtd)}×</Quantidade>
          {valor.descricao && <Descricao>{valor.descricao}</Descricao>}
        </>
      );
    case "texto":
      return coluna.tipo === TIPO_COLUNA.CLIENTE ? <span title={valor.texto}>{valor.texto}</span> : valor.texto;
    default:
      return null;
  }
}

function classeDaColuna(coluna) {
  switch (coluna.tipo) {
    case TIPO_COLUNA.CLIENTE: return "cliente";
    case TIPO_COLUNA.PROJETO: return "projeto";
    case TIPO_COLUNA.LIMITE: return "limite";
    case TIPO_COLUNA.ENTREGA: return "entrega";
    case TIPO_COLUNA.ITEM_DESCRITO: return "item-descrito";
    default: return undefined;
  }
}

function CabecalhoDaColuna({ coluna }) {
  return (
    <div className={classeDaColuna(coluna)}>
      {coluna.tipo === TIPO_COLUNA.ENTREGA && <Ponto aria-hidden="true" />}
      {coluna.rotulo}
    </div>
  );
}

/**
 * @param folha       folha do domínio (utils/apontamentos.montarFolhas)
 * @param logo        { src?, nome } da unidade
 * @param via         { numero, data } quando é 2ª via em diante
 * @param celulasComAviso Set "chaveDoAmbiente|coluna" — contorno só na tela
 *
 * @param ajuste      { nivel, escala } — degrau do ajuste quando o conteúdo
 *                    não cabe (medido no DOM: `[data-corpo]` é a altura útil,
 *                    `[data-dados]` cada linha com ambiente)
 */
export default function FolhaSetor({ folha, logo, via, celulasComAviso, ajuste = AJUSTE_INICIAL }) {
  const { setor, lote, cabecalho } = folha;
  const variavel = setor.colunas.some((coluna) => coluna.tipo === TIPO_COLUNA.ITEM_DESCRITO);
  const propsDaLinha = { $grade: setor.grade, $tint: setor.cor.tint, $deep: setor.cor.deep, $variavel: variavel, $compacta: setor.compacta };
  const longo = setor.titulo.length > 14;

  return (
    <Papel data-folha={folha.chave} aria-label={`${setor.titulo}, lote ${lote.rotulo}`}>
      <Cabecalho>
        <LogoDaUnidade logo={logo} />
        <Divisor aria-hidden="true" />
        <BlocoTitulo>
          <Sobretitulo $cor={setor.cor.accent}>Apontamento gerencial · Diário</Sobretitulo>
          <Titulo $tamanho={longo ? 30 : 34}>{setor.titulo}</Titulo>
        </BlocoTitulo>
        <TrilhaDoFluxo setorAtual={setor} />
        <Selo cor={setor.cor.accent} numero={lote.numero} />
      </Cabecalho>

      <Faixa>
        <Painel>
          <Celula>
            <Rotulo>Etiqueta</Rotulo>
            <Valor>{cabecalho.etiqueta || "—"}</Valor>
          </Celula>
          <Celula>
            <Rotulo>Otimização</Rotulo>
            <Valor>{formatarData(cabecalho.otimizacao)}</Valor>
            <Detalhe>{diaDaSemanaPorExtenso(cabecalho.otimizacao)}</Detalhe>
          </Celula>
          <TerceiraCelula setor={setor} cabecalho={cabecalho} />
        </Painel>
        <CartaoData rotulo={setor.rotuloLimite} iso={cabecalho.limite} fundo={setor.cor.accent} />
        <CartaoData rotulo="Entrega no cliente" iso={cabecalho.entrega} fundo={PAPEL.tinta} comPonto />
      </Faixa>

      <Tabela $compacta={setor.compacta} $nivel={ajuste.nivel} $escala={ajuste.escala} role="table">
        <LinhaCabecalho $grade={setor.grade} $cor={setor.cor.accent} $deep={setor.cor.deep} role="row">
          {setor.colunas.map((coluna) => <CabecalhoDaColuna key={coluna.id} coluna={coluna} />)}
        </LinhaCabecalho>
        <div data-corpo="" style={{ flex: "1 1 0", minHeight: 0, overflow: "hidden" }}>
          <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {folha.linhas.map((linha) => (
              <Linha key={linha.chave} {...propsDaLinha} role="row" data-dados="">
                {setor.colunas.map((coluna) => {
                  const valor = linha.celulas[coluna.id];
                  // Quantidade com descrição alinha à esquerda; "×" e vazio ficam no centro, como no papel.
                  const base = coluna.tipo === TIPO_COLUNA.ITEM_DESCRITO && valor.tipo !== "qtd" ? undefined : classeDaColuna(coluna);
                  const classe = [base, celulasComAviso?.has(`${linha.chave}|${coluna.id}`) && "com-aviso"]
                    .filter(Boolean).join(" ") || undefined;
                  return (
                    <div key={coluna.id} className={classe} role="cell">
                      <ConteudoDaCelula coluna={coluna} valor={valor} setor={setor} />
                    </div>
                  );
                })}
              </Linha>
            ))}
            {Array.from({ length: folha.linhasEmBranco }, (_, i) => (
              <Linha key={`branca-${i}`} {...propsDaLinha} $branca aria-hidden="true">
                {setor.colunas.map((coluna) => <div key={coluna.id} className={classeDaColuna(coluna)} />)}
              </Linha>
            ))}
          </div>
        </div>
      </Tabela>

      <Observacoes $altura={ajuste.nivel >= ULTIMO_DEGRAU ? ALTURA_OBSERVACOES_COMPACTA : undefined}>
        <Rotulo>Observações</Rotulo>
        <Pauta />
      </Observacoes>

      <Rodape>
        <CampoRodape>Preenchido por <span className="linha" /></CampoRodape>
        <CampoRodape $largura={300}>Conferido por <span className="linha" /></CampoRodape>
        <CampoRodape $largura={170}>Data <span className="linha" /></CampoRodape>
      </Rodape>
      {via && via.numero > 1 && <MarcaDeVia>{via.numero}ª via · {via.data}</MarcaDeVia>}
    </Papel>
  );
}
